import React, { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PHOTOS } from "@/lib/data";
import { toast } from "sonner";

const Remembrance = () => {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [candles, setCandles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet("/api/candles")
      .then((d) => setCandles(d.candles || []))
      .catch(() => {});
  }, []);

  const light = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) {
      toast.error("Tell us who the candle is for — even just a first name.");
      return;
    }
    setBusy(true);
    try {
      const candle = await apiPost("/api/candles", { name: name.trim(), message: message.trim() });
      setCandles([candle, ...candles].slice(0, 24));
      setName("");
      setMessage("");
      toast.success("Your candle is lit for the evening of January 14th.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="remembrance"
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{ background: "radial-gradient(ellipse 60% 55% at 50% 0%, #1c1631 0%, #0B1021 72%)" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              align="left"
              eyebrow="A New Year Tradition"
              title={
                <>
                  Light a candle for the ones <em className="text-gold-gradient italic">you'll be honoring</em>
                </>
              }
              sub="Every seat on January 14th holds a story. Before the show, add the name of someone you carry with you — their candle joins the Candlelight Remembrance, glowing across the historic Saban stage during the most tender moment of the evening."
            />

            {/* Candle widget */}
            <Reveal delay={0.15}>
              <form onSubmit={light} className="glass rounded-3xl p-7 mt-9 border-[#F59E0B]/20">
                <div className="flex items-center gap-4">
                  <span className="relative shrink-0 w-12 h-12 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center">
                    <Flame size={22} className="text-[#F59E0B] flame-flicker" />
                  </span>
                  <div>
                    <p className="text-[#FDFBF7] font-medium text-sm">Light a Virtual Candle</p>
                    <p className="text-xs text-[#8A99B5] mt-1">
                      It will glow on this wall, and on the remembrance tree at the venue.
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <input
                    data-testid="candle-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Their name — 'Grandpa Ray'…"
                    maxLength={40}
                    className="w-full bg-[#131B36]/80 border border-white/10 rounded-full px-5 py-3 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/70 focus:outline-none focus:border-[#F59E0B]/60 transition-colors"
                  />
                  <input
                    data-testid="candle-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="A few words (optional)…"
                    maxLength={60}
                    className="w-full bg-[#131B36]/80 border border-white/10 rounded-full px-5 py-3 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/70 focus:outline-none focus:border-[#F59E0B]/60 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="candle-light-submit-btn"
                  className="btn-gold w-full sm:w-auto rounded-full px-8 py-3.5 text-sm font-bold mt-5 disabled:opacity-60"
                >
                  {busy ? "Lighting…" : "Light the Candle"}
                </button>
              </form>
            </Reveal>
          </div>

          <div>
            <Reveal delay={0.1}>
              <div className="rounded-[28px] overflow-hidden gold-ring relative">
                <img
                  src={PHOTOS.candlelight}
                  alt="Warm candlelight glowing softly in remembrance"
                  className="w-full aspect-[16/10] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1021] via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <p className="font-display italic text-xl text-[#FDFBF7]">
                    "As long as we speak their names, they are here."
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Wall of names */}
            <Reveal delay={0.2}>
              <div className="glass rounded-3xl p-7 mt-6 border-[#F59E0B]/15" data-testid="remembrance-wall">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F59E0B]">
                  The Wall of Light
                </p>
                {candles.length === 0 ? (
                  <p className="text-sm text-[#8A99B5] font-light mt-4 leading-relaxed">
                    The wall is dark, waiting. Be the first light for January 14th —
                    light a candle above and their name will glow here.
                  </p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3 mt-5 max-h-64 overflow-y-auto pr-1">
                    {candles.map((c) => (
                      <div key={c.id} className="flex items-start gap-3 border border-white/10 rounded-2xl px-4 py-3 bg-[#131B36]/60">
                        <span className="relative shrink-0 mt-0.5" aria-hidden="true">
                          <Flame size={16} className="text-[#F59E0B] flame-flicker" style={{ animationDelay: `${(c.id.length % 8) * 0.3}s` }} />
                        </span>
                        <div>
                          <p className="text-sm text-[#FDFBF7]">{c.name}</p>
                          {c.message && <p className="text-xs text-[#8A99B5] font-light italic mt-0.5">{c.message}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Remembrance;
